// LoRaWAN gateways of a site: register (EUI, name, location), status from
// ChirpStack (online, offline, never seen), edit and remove.
import { useState, type SyntheticEvent } from 'react';
import type { LorawanGateway } from '../api/types.js';
import { useApi } from '../auth.js';
import { Age, ErrorBox, Spinner } from '../components.js';
import { useResource } from '../hooks.js';
import { PLACEHOLDER } from '../lib/format.js';
import { gatewayEuiError, normalizeHex, parseLocation } from '../lib/lorawan.js';

const STATE: Record<NonNullable<LorawanGateway['state']>, { label: string; cls: string }> = {
  online: { label: 'Online', cls: 'badge-online' },
  offline: { label: 'Offline', cls: 'badge-offline' },
  never_seen: { label: 'Never seen', cls: 'badge-unknown' },
};

function GatewayForm({
  siteId,
  gateway,
  onDone,
  onCancel,
}: {
  siteId: string;
  gateway?: LorawanGateway;
  onDone: () => void;
  onCancel: () => void;
}) {
  const api = useApi();
  const [eui, setEui] = useState('');
  const [name, setName] = useState(gateway?.name ?? '');
  const [description, setDescription] = useState(gateway?.description ?? '');
  const [lat, setLat] = useState(gateway?.location ? String(gateway.location.latitude) : '');
  const [lon, setLon] = useState(gateway?.location ? String(gateway.location.longitude) : '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const euiError = gateway ? null : gatewayEuiError(eui);
  const location = parseLocation(lat, lon);
  const valid = name.trim() !== '' && euiError === null && location.ok;

  async function submit(e: SyntheticEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!location.ok || !valid) return;
    setBusy(true);
    setError(null);
    try {
      const body = {
        name: name.trim(),
        description: description.trim(),
        location: location.value,
      };
      if (gateway) await api.updateLorawanGateway(siteId, gateway.gateway_eui, body);
      else await api.createLorawanGateway(siteId, { gateway_eui: normalizeHex(eui), ...body });
      onDone();
    } catch (err) {
      setError(err instanceof Error ? err : new Error(String(err)));
    } finally {
      setBusy(false);
    }
  }

  return (
    <form
      className="card edge-form"
      onSubmit={(e) => {
        void submit(e);
      }}
    >
      <h2>{gateway ? `Edit ${gateway.name}` : 'Add gateway'}</h2>
      {gateway ? (
        <p className="muted small">
          Gateway EUI <span className="mono">{gateway.gateway_eui}</span>
        </p>
      ) : (
        <p className="muted small">
          The EUI is on the gateway’s label or in its web UI. Point the gateway’s packet forwarder
          (Semtech UDP 1700) or LoRa Basics Station (3001) at the Kalvio LoRaWAN server; it shows as
          online after its first status message.
        </p>
      )}
      <div className="edge-form-row">
        {!gateway && (
          <label className="field">
            <span>Gateway EUI</span>
            <input
              type="text"
              className={`mono${eui !== '' && euiError ? ' invalid' : ''}`}
              autoComplete="off"
              spellCheck={false}
              placeholder="0016C001FF10A235"
              value={eui}
              onChange={(e) => {
                setEui(e.target.value);
              }}
            />
            {eui !== '' && euiError && <span className="form-error">{euiError}</span>}
          </label>
        )}
        <label className="field">
          <span>Name</span>
          <input
            type="text"
            maxLength={100}
            placeholder="e.g. Building E, 3rd floor"
            value={name}
            onChange={(e) => {
              setName(e.target.value);
            }}
          />
        </label>
      </div>
      <label className="field">
        <span>Description (optional)</span>
        <input
          type="text"
          maxLength={500}
          placeholder="e.g. Corridor ceiling, PoE from switch 2"
          value={description}
          onChange={(e) => {
            setDescription(e.target.value);
          }}
        />
      </label>
      <div className="edge-form-row">
        <label className="field">
          <span>Latitude (optional)</span>
          <input
            type="text"
            inputMode="decimal"
            placeholder="48.1486"
            value={lat}
            onChange={(e) => {
              setLat(e.target.value);
            }}
          />
        </label>
        <label className="field">
          <span>Longitude (optional)</span>
          <input
            type="text"
            inputMode="decimal"
            placeholder="17.1077"
            value={lon}
            onChange={(e) => {
              setLon(e.target.value);
            }}
          />
        </label>
      </div>
      {!location.ok && (lat !== '' || lon !== '') && <p className="form-error">{location.error}</p>}
      {error && <ErrorBox error={error} />}
      <div className="form-actions">
        <button type="submit" className="btn btn-primary" disabled={busy || !valid}>
          {busy ? 'Saving…' : gateway ? 'Save' : 'Add gateway'}
        </button>
        <button type="button" className="btn" disabled={busy} onClick={onCancel}>
          Cancel
        </button>
      </div>
    </form>
  );
}

export const GATEWAYS_REFRESH_MS = 30_000;

export function GatewaysTab({
  siteId,
  now,
  timeZone,
}: {
  siteId: string;
  now: number;
  timeZone: string;
}) {
  const api = useApi();
  const list = useResource(
    `lorawan-gateways:${siteId}`,
    (s) => api.lorawanGateways(siteId, s),
    GATEWAYS_REFRESH_MS,
  );
  const [form, setForm] = useState<'add' | LorawanGateway | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  async function remove(g: LorawanGateway) {
    if (
      !window.confirm(
        `Remove gateway “${g.name}” (${g.gateway_eui})? It is deleted in ChirpStack too; sensors only it receives go silent.`,
      )
    ) {
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await api.deleteLorawanGateway(siteId, g.gateway_eui);
      list.reload();
    } catch (e) {
      setError(e instanceof Error ? e : new Error(String(e)));
    } finally {
      setBusy(false);
    }
  }

  const done = () => {
    setForm(null);
    list.reload();
  };

  return (
    <>
      <div className="section-head">
        <h2>Gateways</h2>
        <div className="page-actions">
          <button
            type="button"
            className="btn btn-primary"
            disabled={form !== null}
            onClick={() => {
              setForm('add');
            }}
          >
            Add gateway
          </button>
        </div>
      </div>
      {form !== null && (
        <GatewayForm
          key={form === 'add' ? 'add' : form.gateway_eui}
          siteId={siteId}
          {...(form !== 'add' && { gateway: form })}
          onDone={done}
          onCancel={() => {
            setForm(null);
          }}
        />
      )}
      {error && <ErrorBox error={error} />}
      {list.error && <ErrorBox error={list.error} onRetry={list.reload} />}
      {list.data?.chirpstack_error && (
        <p className="warn small">ChirpStack: {list.data.chirpstack_error}</p>
      )}
      {!list.data && list.loading && <Spinner />}
      {list.data && list.data.items.length === 0 && (
        <p className="empty">No gateway registered for this site yet.</p>
      )}
      {list.data && list.data.items.length > 0 && (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>Gateway</th>
                <th>State</th>
                <th>Last seen</th>
                <th>Location</th>
                <th>
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {list.data.items.map((g) => {
                const st = g.state ? STATE[g.state] : null;
                return (
                  <tr key={g.gateway_eui}>
                    <td>
                      <div className="strong">{g.name}</div>
                      <div className="muted small mono">{g.gateway_eui}</div>
                      {g.description && <div className="muted small">{g.description}</div>}
                      {g.in_chirpstack === false && (
                        <div className="warn small">not in ChirpStack</div>
                      )}
                    </td>
                    <td>
                      {st ? (
                        <span className={`badge ${st.cls}`}>
                          <span className="dot" aria-hidden="true" />
                          {st.label}
                        </span>
                      ) : (
                        PLACEHOLDER
                      )}
                    </td>
                    <td>
                      <Age ts={g.last_seen} now={now} timeZone={timeZone} />
                    </td>
                    <td className="small mono">
                      {g.location
                        ? `${g.location.latitude.toFixed(5)}, ${g.location.longitude.toFixed(5)}`
                        : PLACEHOLDER}
                    </td>
                    <td>
                      <div className="row-actions">
                        <button
                          type="button"
                          className="btn btn-small"
                          disabled={busy || form !== null}
                          onClick={() => {
                            setForm(g);
                          }}
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          className="btn btn-small"
                          disabled={busy}
                          onClick={() => {
                            void remove(g);
                          }}
                        >
                          Remove
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}
